// Axios adapter for demo mode. Replaces network I/O on the shared `api` instance
// with the in-browser mock router. Returns axios-shaped responses; non-2xx are
// rejected with `error.response` set so existing interceptors/UI behave normally.

import { handle } from "./router";

function combineUrl(config) {
  let url = String(config.url || "");
  if (/^https?:\/\//i.test(url)) return url;
  const base = String(config.baseURL || "").replace(/\/+$/, "");
  const rel = url.startsWith("/") ? url : `/${url}`;
  return base + rel;
}

function parsePathAndQuery(config) {
  const full = combineUrl(config);
  let pathname;
  let search = "";
  try {
    if (/^https?:\/\//i.test(full)) {
      const u = new URL(full);
      pathname = u.pathname;
      search = u.search;
    } else {
      const [p, q = ""] = full.split("?");
      pathname = p;
      search = q ? `?${q}` : "";
    }
  } catch {
    const [p, q = ""] = full.split("?");
    pathname = p;
    search = q ? `?${q}` : "";
  }
  if (pathname.startsWith("/api")) pathname = pathname.slice(4) || "/";
  pathname = pathname.replace(/\/+$/, "") || "/";

  // Merge query-string params with any config.params object.
  const params = { ...(config.params || {}) };
  if (search) {
    const sp = new URLSearchParams(search.replace(/^\?/, ""));
    for (const [k, v] of sp.entries()) {
      if (!(k in params)) params[k] = v;
    }
  }
  return { path: pathname, params };
}

function parseBody(config) {
  const data = config.data;
  if (data == null) return undefined;
  if (typeof data === "string") {
    try {
      return JSON.parse(data);
    } catch {
      return data;
    }
  }
  return data;
}

function delay() {
  const ms = 120 + Math.floor(Math.random() * 230);
  return new Promise((r) => setTimeout(r, ms));
}

export function createDemoAdapter() {
  return async function demoAdapter(config) {
    const method = String(config.method || "get").toLowerCase();
    const { path, params } = parsePathAndQuery(config);
    const body = parseBody(config);

    await delay();

    let result;
    try {
      result = await handle({ method, path, body, params });
    } catch (e) {
      result = { status: 500, data: { detail: "Demo data error", message: String(e && e.message) } };
    }

    const status = Number(result.status || 200);
    const response = {
      data: result.data,
      status,
      statusText: status >= 200 && status < 300 ? "OK" : "Error",
      headers: {},
      config,
      request: { demo: true },
    };

    const validate = config.validateStatus || ((s) => s >= 200 && s < 300);
    if (validate(status)) {
      return response;
    }
    const error = new Error(`Request failed with status code ${status}`);
    error.config = config;
    error.request = response.request;
    error.response = response;
    error.isAxiosError = true;
    throw error;
  };
}
